import { IS_AWS_ENV } from "constants/app.constants";
import {
  wellCountryToRigFracCountries,
  wellCountyToRigFracCounties,
  wellStateToRigFracStates
} from "utils/map/rigFracValueMapping";

// The well focus (group-by) fields that turn on rig/frac colour syncing,
// matched against IGroupBy.property only, never the displayed label. The
// near-miss properties Header.OperatorName (the facility focus default),
// Header.OriginalOperator and Header.OriginalOperator_Short are deliberately
// absent from this table — they must never trigger this feature.
export const FOCUS_SYNC_OPERATOR_PROPERTY = "Header.Operator";
export const FOCUS_SYNC_OPERATOR_SHORT_PROPERTY = "Header.Operator_Short";
export const FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY =
  "Header.Operator_ExchangeStockSymbol";
export const FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY = "Header.Operator_StockSymbol";
export const FOCUS_SYNC_BASIN_PROPERTY = "Header.Basin";
export const FOCUS_SYNC_RESOURCE_PLAY_PROPERTY = "Header.ResourcePlay";
export const FOCUS_SYNC_FIELD_COUNTY_PROPERTY = "Header.FieldCounty";
export const FOCUS_SYNC_STATE_PROVINCE_PROPERTY = "Header.StateProvince";
export const FOCUS_SYNC_COUNTRY_PROPERTY = "Header.Country";

// The rig/frac attribute a trigger matches against — the layer's Color By
// key, or the chart's own group-by dimension. Never the well-side property
// name. A plain string union rather than an import of RigFracColorByField
// keeps this module free of a dependency on components/ — callers compare
// their own field key against this with plain string equality.
export type RigFracFocusAttribute =
  | "operator"
  | "basin"
  | "subBasin"
  | "county"
  | "stateProvince"
  | "country";

export interface FocusSyncTrigger {
  property: string;
  attribute: RigFracFocusAttribute;
  // True for the three operator variants whose values (a short name, an
  // exchange:symbol pair, a bare symbol) never appear verbatim on a rig or
  // frac record — they need translating to the public-record operator name
  // before they can be matched. False for every other trigger.
  needsTranslation: boolean;
  // Rewrites one well value to the rig/frac spellings it can match, for a
  // field whose vocabularies differ only in format. Absent = matched verbatim.
  toRigFracValues?: (value: string) => string[];
}

const OPERATOR_TRIGGERS: readonly FocusSyncTrigger[] = [
  {
    property: FOCUS_SYNC_OPERATOR_PROPERTY,
    attribute: "operator",
    needsTranslation: false
  },
  {
    property: FOCUS_SYNC_OPERATOR_SHORT_PROPERTY,
    attribute: "operator",
    needsTranslation: true
  },
  {
    property: FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY,
    attribute: "operator",
    needsTranslation: true
  },
  {
    property: FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY,
    attribute: "operator",
    needsTranslation: true
  }
];

// Azure well headers carry basin names in Header.ResourcePlay.
export const AZURE_FOCUS_SYNC_TRIGGERS: readonly FocusSyncTrigger[] = [
  ...OPERATOR_TRIGGERS,
  {
    property: FOCUS_SYNC_RESOURCE_PLAY_PROPERTY,
    attribute: "basin",
    needsTranslation: false
  }
];

// AWS well headers carry sub-basin names in Header.ResourcePlay, basins in
// Header.Basin, "Name, ST" counties in Header.FieldCounty, "TX"-style codes
// in Header.StateProvince and "US" in Header.Country (EVA-338).
export const AWS_FOCUS_SYNC_TRIGGERS: readonly FocusSyncTrigger[] = [
  ...OPERATOR_TRIGGERS,
  {
    property: FOCUS_SYNC_BASIN_PROPERTY,
    attribute: "basin",
    needsTranslation: false
  },
  {
    property: FOCUS_SYNC_RESOURCE_PLAY_PROPERTY,
    attribute: "subBasin",
    needsTranslation: false
  },
  {
    property: FOCUS_SYNC_FIELD_COUNTY_PROPERTY,
    attribute: "county",
    needsTranslation: false,
    toRigFracValues: wellCountyToRigFracCounties
  },
  {
    property: FOCUS_SYNC_STATE_PROVINCE_PROPERTY,
    attribute: "stateProvince",
    needsTranslation: false,
    toRigFracValues: wellStateToRigFracStates
  },
  {
    property: FOCUS_SYNC_COUNTRY_PROPERTY,
    attribute: "country",
    needsTranslation: false,
    toRigFracValues: wellCountryToRigFracCountries
  }
];

function byProperty(
  triggers: readonly FocusSyncTrigger[]
): ReadonlyMap<string, FocusSyncTrigger> {
  return new Map(triggers.map((trigger) => [trigger.property, trigger]));
}

const AZURE_TRIGGERS_BY_PROPERTY = byProperty(AZURE_FOCUS_SYNC_TRIGGERS);
const AWS_TRIGGERS_BY_PROPERTY = byProperty(AWS_FOCUS_SYNC_TRIGGERS);

// Read per call rather than at module load, so tests can flip IS_AWS_ENV.
export function getFocusSyncTriggers(): readonly FocusSyncTrigger[] {
  return IS_AWS_ENV ? AWS_FOCUS_SYNC_TRIGGERS : AZURE_FOCUS_SYNC_TRIGGERS;
}

/**
 * Looks up the trigger row for a well focus property, by exact string match
 * only. Returns undefined for every other property, including a near-miss
 * one that only differs from a real trigger by a legal suffix or an "s".
 */
export function getFocusSyncTrigger(property: Maybe<string>): Maybe<FocusSyncTrigger> {
  if (property == null) return undefined;
  return (IS_AWS_ENV ? AWS_TRIGGERS_BY_PROPERTY : AZURE_TRIGGERS_BY_PROPERTY).get(
    property
  );
}
